import { useState } from 'react';
import { useRouter } from 'next/router';

import { Accordion, Box, Heading, Icon, Paragraph, Text, TextButton, Upsell } from '@zillow/constellation-10';
import { IconAwardRibbonFilled } from '@zillow/constellation-icons';

import { useRentalManagerUserFreemiumWebApiHandler_isTopOfFunnelEnrollmentEligible } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import accessUtils from '../../../../utils/accessUtils';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../../constants/FreemiumConstants';

import SFCostAndFeesTotalMonthlyPriceWithFeeAlert from '../../../../components/Alerts/SFCostAndFeesTotalMonthlyPriceWithFeeAlert';
import PremiumPurchaseModal from '../../../Modal/PremiumPurchaseModal';
import SFTotalMonthlyPriceWithFeeAcknowledgementModal from '../../../modals/SFTotalMonthlyPriceWithFeeAcknowledgementModal';
import AiDraftingFooterStatus from '../../components/AiDraftingFooterStatus';

import {
  wizardFlexContainerStyles,
  wizardFormSectionContainerStyles,
  wizardHeadingStyles,
  wizardParagraphStyles,
} from '../../../../styles/wizard';

import PremiumPaymentGateway from '../../../PaymentGateway/PremiumPaymentGateway';
import CostsAndFeesFormField from '../../../PostingPath/CostsAndFeesFormField';
import DateAvailableFormField from '../../../PostingPath/DateAvailableFormField';
import MonthlyRentFormField from '../../../PostingPath/MonthlyRentFormField';
import SecurityDepositFormField from '../../../PostingPath/SecurityDepositFormField';
import SpecialOffers from '../../../PostingPath/SpecialOffers';
import SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert from '../../../SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert';
import PageWizard from '../../../Wizard/PageWizard';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const RentDetailsAndCosts = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const isFeed = listingDetails?.isFeed ?? false;
  const isMultifamily = listingDetails?.isMultifamily ?? false;

  const canAccessSFCostsAndFees = accessUtils.canAccessSFCostsAndFees({ isFeed, isMultifamily });
  const canAccessFastPost = accessUtils.canAccessFastPost({
    isMultifamily,
    lastActivatedMs: listingDetails?.lastActivatedMs,
    isFeed,
    restrictions: listingDetails?.restrictions ?? { applications: { isAccessRestricted: false } },
  });

  const isActiveProductTypePremium = listingDetails?.freemium?.activeProduct?.type === PRODUCT_TYPE_NAME_TO_ID.PREMIUM;

  const { data: isTopOfFunnelEnrollmentEligible = false } =
    useRentalManagerUserFreemiumWebApiHandler_isTopOfFunnelEnrollmentEligible({
      enabled: !isActiveProductTypePremium,
      meta: { tags: ['isTopOfFunnelEnrollmentEligible'], context: { api: 'freemium', details: 'ListingPage' } },
    });

  const canAccessTopOfFunnelPremium = accessUtils.canAccessTopOfFunnelPremium({
    isFreemiumEnabled: listingDetails?.freemium?.isEnabled ?? false,
    isTopOfFunnelEnrollmentEligible,
    isActiveProductTypePremium,
  });

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible ?? false,
  });

  const isOptInTotalMonthlyPriceWithFeeEligible = listingDetails?.totalMonthlyPriceWithFee?.isOptInEligible ?? false;
  const displayTotalMonthlyPriceWithFee = listingDetails?.displayTotalMonthlyPriceWithFee ?? false;

  const [showAcknowledgementModal, setShowAcknowledgementModal] = useState(false);
  const [displayPremiumPurchaseModal, setDisplayPremiumPurchaseModal] = useState(false);
  const [displayPremiumPaymentGateway, setDisplayPremiumPaymentGateway] = useState(false);
  const [tipsExpanded, setTipsExpanded] = useState<Array<string>>([]);

  const handleTipsExpandedChange = (value: string | Array<string>) => {
    setTipsExpanded(typeof value === 'string' ? [value] : value);
  };

  const onNextClick = () => {
    if (canAccessSFCostsAndFees && canAccessTotalMonthlyPriceWithFee && !displayTotalMonthlyPriceWithFee) {
      setShowAcknowledgementModal(true);
      return;
    }
    onGoToNextStep?.();
  };

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      footerContent={<AiDraftingFooterStatus />}
      {...rest}
    >
      <div data-testid="RentDetailsAndCosts">
        <Box css={wizardFlexContainerStyles}>
          <Box data-testid="FormSectionContainer" css={wizardFormSectionContainerStyles.raw()}>
            <Heading textStyle="heading-md" level={2} css={wizardHeadingStyles}>
              Rent, costs, and fees
            </Heading>
            <Paragraph css={{ marginBottom: '600' }}>
              Set the rent, deposit, and any additional fees renters will pay. Clear, complete pricing helps you attract
              qualified applicants and meet fee transparency laws.
            </Paragraph>
            <Heading textStyle="heading-xs" level={2} css={wizardHeadingStyles}>
              {canAccessTotalMonthlyPriceWithFee
                ? 'How much are the base rent and the security deposit?'
                : 'How much are the monthly rent and the security deposit?'}
            </Heading>
            <Paragraph css={wizardParagraphStyles}>
              A security deposit isn&apos;t required, but you can choose to collect one.
            </Paragraph>
            <MonthlyRentFormField />

            {!isActiveProductTypePremium && canAccessTopOfFunnelPremium && (
              <Upsell
                css={{ marginTop: '800', marginBottom: '800' }}
                elevated={false}
                tone="soft"
                header={
                  <Heading textStyle="heading-sm" level={3}>
                    Optimize your listing with {listingDetails?.city} pricing data and insights
                  </Heading>
                }
                body={
                  <Paragraph>
                    Premium gives you access to deeper insights and analytics to help price your rental and optimize
                    your listing.
                  </Paragraph>
                }
                media={<Icon render={<IconAwardRibbonFilled />} />}
                closeable={false}
                action={<TextButton onClick={() => setDisplayPremiumPurchaseModal(true)}>Learn more</TextButton>}
              />
            )}

            <SecurityDepositFormField />
            <Box css={{ marginTop: '800' }}>
              <SpecialOffers />
            </Box>
            {!canAccessFastPost && (
              <>
                <Heading level={2} textStyle="heading-xs" css={wizardHeadingStyles}>
                  When is your property available to rent?
                </Heading>
                <DateAvailableFormField />
              </>
            )}

            {canAccessSFCostsAndFees && (
              <>
                <Heading textStyle="heading-xs" level={2} css={wizardHeadingStyles}>
                  What additional fees do you charge?
                </Heading>
                <Paragraph css={wizardParagraphStyles}>
                  Adding fees and showing the total price on your listing helps renters know the real cost of renting
                  your property before they even apply.
                </Paragraph>
                {canAccessTotalMonthlyPriceWithFee && <SFCostAndFeesTotalMonthlyPriceWithFeeAlert />}
                {isOptInTotalMonthlyPriceWithFeeEligible && (
                  <SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert triggerLocation="posting_path" />
                )}
                <CostsAndFeesFormField displayedLocation="posting_path" />
              </>
            )}

            {canAccessSFCostsAndFees && (
              <Accordion.Root
                expanded={tipsExpanded}
                onExpandedChange={handleTipsExpandedChange}
                css={{ marginTop: '800' }}
              >
                <Accordion.Item value="costs-tips">
                  <Accordion.Header>
                    <Heading level={5}>Tips for adding costs and fees </Heading>
                  </Accordion.Header>
                  <Accordion.Panel>
                    <Box css={{ display: 'flex', flexDirection: 'column', gap: '600' }}>
                      {canAccessTotalMonthlyPriceWithFee && (
                        <>
                          <Box>
                            <Text textStyle="body-bold">Capture required monthly fees</Text>
                            <Paragraph>
                              Make sure to add any required monthly fees, and note if any are already included in the
                              base rent.
                            </Paragraph>
                          </Box>
                          <Box>
                            <Text textStyle="body-bold">Fee transparency helps renters and landlords</Text>
                            <Paragraph>
                              Fee transparency helps renters make informed decisions and improves efficiency for you
                              when placing a renter.
                            </Paragraph>
                          </Box>
                        </>
                      )}
                      {isOptInTotalMonthlyPriceWithFeeEligible && (
                        <>
                          <Box>
                            <Text textStyle="body-bold">Clarify if fees are included</Text>
                            <Paragraph>
                              Required monthly fees can be included in your base rent or added separately here.
                            </Paragraph>
                          </Box>
                          <Box>
                            <Text textStyle="body-bold">Opt to show the total monthly price</Text>
                            <Paragraph>
                              Once your required monthly fees are accounted for, we recommend showing the total monthly
                              price on your listing. This gives renters a better idea of the true cost of renting your
                              property.
                            </Paragraph>
                          </Box>
                        </>
                      )}
                      {!canAccessTotalMonthlyPriceWithFee && !isOptInTotalMonthlyPriceWithFeeEligible && (
                        <>
                          <Box>
                            <Text textStyle="body-bold">Only add the fees you need</Text>
                            <Paragraph>Note which are included in the base rent.</Paragraph>
                          </Box>
                          <Box>
                            <Text textStyle="body-bold">Check your local requirements</Text>
                            <Paragraph>
                              Some states require disclosing certain fees — be sure to check what&apos;s required for
                              your area.
                            </Paragraph>
                          </Box>
                        </>
                      )}
                      <Box>
                        <Text textStyle="body-bold">Save the application fee for later</Text>
                        <Paragraph>
                          You&apos;ll answer this when we check whether you&apos;re using Zillow applications.
                        </Paragraph>
                      </Box>
                    </Box>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion.Root>
            )}
          </Box>
        </Box>
      </div>

      {showAcknowledgementModal && (
        <SFTotalMonthlyPriceWithFeeAcknowledgementModal
          onClose={() => setShowAcknowledgementModal(false)}
          onSubmit={() => {
            setShowAcknowledgementModal(false);
            onGoToNextStep?.();
          }}
        />
      )}
      {displayPremiumPurchaseModal && (
        <PremiumPurchaseModal
          onClose={() => setDisplayPremiumPurchaseModal(false)}
          onSelectUpgrade={() => {
            setDisplayPremiumPurchaseModal(false);
            setDisplayPremiumPaymentGateway(true);
          }}
          freemiumProducts={listingDetails?.freemium?.products ?? []}
          isInBuildingGroup={listingDetails?.freemium?.isInBuildingGroup ?? false}
        />
      )}
      {displayPremiumPaymentGateway && (
        <PremiumPaymentGateway
          listingAlias={listingDetails?.listingAlias ?? ''}
          isActive={listingDetails?.active ?? false}
          isInBuildingGroup={listingDetails?.freemium?.isInBuildingGroup ?? false}
          freemiumData={listingDetails?.freemium ?? undefined}
          isPremiumMidfunnelPayment={true}
          displayContext="NEXT_STEP"
          onClose={() => setDisplayPremiumPaymentGateway(false)}
        />
      )}
    </PageWizard>
  );
};

export default RentDetailsAndCosts;
